import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Descriptions, Spin, Table, Typography } from 'antd';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useGlobalContext } from '../../store/GlobalContext';
import { amount } from './format';

const { Title, Paragraph } = Typography;

/** Read-only court figures computed from the estate records and reused by the probate PDFs. */
export default function CourtFiguresPage() {
  const { selectedEstate } = useGlobalContext();
  const estateId = selectedEstate?.uuid || '';
  const [summary, setSummary] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!estateId) return;
    let cancelled = false;
    setLoading(true); setSummary(undefined); setError('');
    api.get(`/summary/${estateId}`)
      .then(({ data }) => { if (!cancelled) { setSummary(data); setError(''); } })
      .catch(() => { if (!cancelled) setError('Unable to load court financial data'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [estateId, attempt]);

  if (!estateId) return null;

  return (
    <div className="court-figures" style={{ padding: 24 }}>
      <Title level={3}>Court Figures</Title>
      <Paragraph type="secondary">
        Computed from assets, liabilities and cashflows. These figures fill the court documents; change them by updating the underlying records.
      </Paragraph>
      {loading ? <Spin /> : error ? <Alert type="error" message={error} action={<Button onClick={() => setAttempt(attempt + 1)}>Retry</Button>} /> : <>
        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
        {(summary?.unclassifiedAssetCount > 0 || summary?.unknownJurisdictionCount > 0) && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`${summary.unclassifiedAssetCount} assets need classification; ${summary.unknownJurisdictionCount} real properties need a jurisdiction. Incomplete type totals remain blank in forms.`} />}
        {summary?.incompleteRentalCount > 0 && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`${summary.incompleteRentalCount} rental cashflows need a linked asset and valid schedule before rents can be projected.`} />}

        <Card title="Petition figures">
          <Descriptions column={{ xs: 1, md: 2 }} bordered size="small" items={[
            { key: 'personal', label: 'Personal property', children: <span className="financial-value">{amount(summary?.petitionPersonalProperty)}</span> },
            { key: 'improved', label: 'Improved real property in NY', children: <span className="financial-value">{amount(summary?.petitionImprovedRealPropertyNY)}</span> },
            { key: 'raw', label: 'Unimproved real property in NY', children: <span className="financial-value">{amount(summary?.petitionUnimprovedRealPropertyNY)}</span> },
            { key: 'range', label: 'Petition range (gross value ±5%)', children: <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}><span className="financial-value">{amount(summary?.petitionLowerValue)}</span><span>to</span><span className="financial-value">{amount(summary?.petitionUpperValue)}</span></div> },
            { key: 'net', label: 'Remaining assets net of recorded liens', children: <span className="financial-value">{amount(summary?.netRemainingAssetValue)}</span> },
            { key: 'distributed', label: 'Recorded distributions to beneficiaries', children: <span className="financial-value">{amount(summary?.beneficiariesDistributedValue)}</span> },
          ]} />
        </Card>

        <Card title="Projected 18-month gross rents" style={{ marginTop: 16 }} extra={<strong>{amount(summary?.projectedGrossRents18Months)}</strong>}>
          <Paragraph>
            Rent projection: {summary?.rentalProjectionFrom} through {summary?.rentalProjectionTo} (end date excluded). Based on cashflows marked gross rental income and linked to assets still held.
          </Paragraph>
          <Table scroll={{ x: 'max-content' }} size="small" rowKey="name" pagination={false} dataSource={summary?.rentalProjectionRows || []} columns={[
            { title: 'Rental cashflow', dataIndex: 'name' }, { title: 'Occurrences', dataIndex: 'occurrences' },
            { title: 'Amount', dataIndex: 'amount', render: amount }, { title: 'Projection', dataIndex: 'projected', render: amount },
          ]} />
        </Card>

        <Card title="Estate not fully distributed" style={{ marginTop: 16 }}>
          <Descriptions column={{ xs: 1, md: 2 }} bordered size="small" items={[
            { key: 'gross', label: 'Gross at date of death', children: <span className="financial-value">{amount(summary?.reportGrossEstate)}</span> },
            { key: 'distributed', label: 'Distributed to beneficiaries', children: <span className="financial-value">{amount(summary?.reportDistributedToBeneficiaries)}</span> },
            { key: 'remaining', label: 'Current assets still held', children: <span className="financial-value">{amount(summary?.reportRemainingInHands)}</span> },
            { key: 'difference', label: 'Difference to reconcile', children: <span className="financial-value">{amount(summary?.reportReconciliationDifference)}</span> },
          ]} />
          <Paragraph style={{ marginTop: 12 }}>
            Current cash and other holdings must be kept up to date, including sale proceeds. A sale is excluded from beneficiary distributions. Receipts, expenses and changes in value can create a difference from the original gross.
          </Paragraph>
          {(summary?.reportMissingBaselineCount > 0 || summary?.missingDistributionValueCount > 0) && <Alert type="warning" message={`${summary?.reportMissingBaselineCount || 0} date-of-death values and ${summary?.missingDistributionValueCount || 0} beneficiary distribution values remain unrecorded.`} description={<Link to="/assets">Review asset valuations and distributions</Link>} />}
        </Card>
      </>}
    </div>
  );
}
